import { FormFieldType, type TFormField } from '@models/forms';
import type { Item } from '@components/select/Select';

// ── Shared option lists ──

export const DATA_TYPE_ITEMS: Item[] = [
  { value: 'String', label: 'String' },
  { value: 'Integer', label: 'Integer' },
  { value: 'Float', label: 'Float' },
  { value: 'Boolean', label: 'Boolean' },
  { value: 'Date', label: 'Date' },
  { value: 'DateTime', label: 'DateTime' },
  { value: 'Currency', label: 'Currency' },
];

export const UI_FIELD_TYPE_ITEMS: Item[] = [
  { value: '', label: 'None' },
  { value: 'Textbox', label: 'Textbox' },
  { value: 'Textarea', label: 'Textarea' },
  { value: 'DropDown', label: 'DropDown' },
  { value: 'RadioButton', label: 'RadioButton' },
  { value: 'Checkbox', label: 'Checkbox' },
  { value: 'DatePicker', label: 'DatePicker' },
  { value: 'CurrencyInput', label: 'CurrencyInput' },
  { value: 'NumericInput', label: 'NumericInput' },
  { value: 'Hidden', label: 'Hidden' },
];

export const BOOL_ITEMS: Item[] = [
  { value: 'false', label: 'No' },
  { value: 'true', label: 'Yes' },
];

const NULLABLE_BOOL_ITEMS: Item[] = [
  { value: '', label: 'Inherit' },
  { value: 'false', label: 'No' },
  { value: 'true', label: 'Yes' },
];

// ── Smart visibility sets (shared by both editors) ──

export const NUMERIC_DATA_TYPES = new Set(['int', 'integer', 'decimal', 'float', 'currency']);
export const VALUE_RANGE_UI_TYPES = new Set(['numericinput', 'currencyinput', 'textbox', 'percentage']);
export const DROPDOWN_UI_TYPES = new Set(['dropdown']);
export const TEXT_UI_TYPES = new Set(['textbox', 'textarea']);

// ── Field context ──

export type FieldContext = 'base' | 'override';

/**
 * Identity fields — only shown for the base attribute editor (not overrides).
 */
export const IDENTITY_FIELDS: Record<string, TFormField> = {
  codeName: {
    name: 'codeName',
    label: 'Code Name',
    fieldType: FormFieldType.TextField,
    required: true,
    gridWidth: { xs: 12, sm: 6, md: 3 },
  },
  label: {
    name: 'label',
    label: 'Label',
    fieldType: FormFieldType.TextField,
    required: true,
    gridWidth: { xs: 12, sm: 6, md: 3 },
  },
  dataType: {
    name: 'dataType',
    label: 'Data Type',
    fieldType: FormFieldType.SelectField,
    required: true,
    gridWidth: { xs: 12, sm: 6, md: 2 },
    fieldConfig: {
      selectField: { options: DATA_TYPE_ITEMS },
    },
  },
  uiFieldType: {
    name: 'uiFieldType',
    label: 'UI Field Type',
    fieldType: FormFieldType.SelectField,
    required: true,
    gridWidth: { xs: 12, sm: 6, md: 2 },
    fieldConfig: {
      selectField: { options: UI_FIELD_TYPE_ITEMS },
    },
  },
  order: {
    name: 'order',
    label: 'Order',
    fieldType: FormFieldType.TextField,
    required: true,
    gridWidth: { xs: 12, sm: 6, md: 2 },
    fieldConfig: {
      textField: { inputType: 'number' },
    },
  },
};

// ── Layout field config with shownFor ──

interface LayoutFieldEntry {
  shownFor: FieldContext[];
  base: TFormField;
  /** Override-specific adjustments — merged on top of `base` when context is 'override'. */
  overrideOverrides?: Partial<TFormField>;
}

const LAYOUT_FIELD_ENTRIES: Record<string, LayoutFieldEntry> = {
  label: {
    shownFor: ['override'],
    base: {
      name: 'label',
      label: 'Label Override',
      fieldType: FormFieldType.TextField,
      gridWidth: { xs: 12, sm: 6, md: 3 },
    },
  },
  // Custom render — Component is injected per render in FieldEditor so it can capture
  // the current list of sibling list-item-group names for the suggestion dropdown.
  // See makeListItemGroupField in ListItemGroupField.tsx.
  listItemGroupName: {
    shownFor: ['base'],
    base: {
      name: 'listItemGroupName',
      label: 'List Item Group',
      fieldType: FormFieldType.CustomField,
      gridWidth: { xs: 12, sm: 6, md: 3 },
      fieldConfig: {
        customField: { Component: () => null },
      },
    },
  },
  uiFieldType: {
    shownFor: ['override'],
    base: {
      name: 'uiFieldType',
      label: 'UI Field Type',
      fieldType: FormFieldType.SelectField,
      gridWidth: { xs: 12, sm: 6, md: 3 },
      fieldConfig: {
        selectField: { options: UI_FIELD_TYPE_ITEMS },
      },
    },
  },
  order: {
    shownFor: ['override'],
    base: {
      name: 'order',
      label: 'Order',
      fieldType: FormFieldType.TextField,
      gridWidth: { xs: 12, sm: 6, md: 2 },
      fieldConfig: {
        textField: { inputType: 'number' },
      },
    },
  },
  fieldColumns: {
    shownFor: ['base', 'override'],
    base: {
      name: 'fieldColumns',
      label: 'Field Columns',
      fieldType: FormFieldType.TextField,
      required: true,
      gridWidth: { xs: 12, sm: 6, md: 2 },
      fieldConfig: {
        textField: { inputType: 'number' },
      },
    },
    overrideOverrides: { required: false },
  },
  requireNewLine: {
    shownFor: ['base', 'override'],
    base: {
      name: 'requireNewLine',
      label: 'Require New Line',
      fieldType: FormFieldType.SelectField,
      gridWidth: { xs: 12, sm: 6, md: 2 },
      fieldConfig: {
        selectField: { options: BOOL_ITEMS },
      },
    },
    overrideOverrides: {
      fieldConfig: {
        selectField: { options: NULLABLE_BOOL_ITEMS },
      },
    },
  },
  allowOverride: {
    shownFor: ['base', 'override'],
    base: {
      name: 'allowOverride',
      label: 'Allow Override',
      fieldType: FormFieldType.SelectField,
      gridWidth: { xs: 12, sm: 6, md: 2 },
      fieldConfig: {
        selectField: { options: BOOL_ITEMS },
      },
    },
  },
  overrideUIFieldType: {
    shownFor: ['base', 'override'],
    base: {
      name: 'overrideUIFieldType',
      label: 'Override UI Field Type',
      fieldType: FormFieldType.TextField,
      gridWidth: { xs: 12, sm: 6, md: 3 },
      watchedFields: ['allowOverride'],
      isVisible: ({ data }) => data?.allowOverride === true || data?.allowOverride === 'true',
    },
  },
  helpText: {
    shownFor: ['base', 'override'],
    base: {
      name: 'helpText',
      label: 'Help Text',
      fieldType: FormFieldType.TextField,
      gridWidth: { xs: 12, sm: 6 },
      fieldConfig: {
        textField: { multiline: true, rows: 2 },
      },
    },
  },
  hoverText: {
    shownFor: ['base', 'override'],
    base: {
      name: 'hoverText',
      label: 'Hover Text',
      fieldType: FormFieldType.TextField,
      gridWidth: { xs: 12, sm: 6 },
      fieldConfig: {
        textField: { multiline: true, rows: 2 },
      },
    },
  },
};

/**
 * Returns the layout TFormField map for a given context.
 * - `'base'`     → fields for the main FieldEditor (excludes override-only fields like label override)
 * - `'override'` → fields for FieldOfferingOverrides (excludes base-only fields like listItemGroupName,
 *                   applies override adjustments like nullable booleans)
 */
export function getLayoutFields(context: FieldContext): Record<string, TFormField> {
  const result: Record<string, TFormField> = {};
  for (const [key, entry] of Object.entries(LAYOUT_FIELD_ENTRIES)) {
    if (!entry.shownFor.includes(context)) continue;
    if (context === 'override' && entry.overrideOverrides) {
      result[key] = { ...entry.base, ...entry.overrideOverrides };
    } else {
      result[key] = entry.base;
    }
  }
  return result;
}
